import React from 'react';
import { 
  Shield, Clock, Award, Users, CheckCircle, Truck, Package, 
  Warehouse, Heart, MapPin, Thermometer
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const About = () => {
  const values = [
    {
      icon: Shield,
      title: 'Reliability',
      description: 'With a 99.8% on-time delivery rate, we deliver on our promises consistently.'
    },
    {
      icon: Clock,
      title: '24/7/365 Operations',
      description: 'Our logistics never sleep. Round-the-clock support for critical deliveries.'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'Industry-leading certifications including TSA, HAZMAT, and HIPAA compliance.'
    },
    {
      icon: Users,
      title: 'Partnership',
      description: 'We work alongside your team as an extension of your operations.'
    }
  ];

  const capabilities = [
    {
      icon: Package,
      title: 'Service Parts Logistics',
      description: 'When equipment goes down, you want to get replacement parts there, pronto.',
      details: 'Our network of forward stocking locations (FSLs), combined with efficient transportation, get parts to your field technicians in no time. Your techs can efficiently repair equipment in the field, reduce customer downtime and help you meet your service commitments.'
    },
    {
      icon: Warehouse,
      title: 'Warehousing & Distribution',
      description: 'With customer expectations constantly changing, you\'ve got to rise to the challenge of an increasingly complex supply chain.',
      details: 'Who says you can\'t manage growth, improve customer experience and keep up with new technology, all while reducing cost and complexity? Not us. We say, "Bring it on." Our distribution network helps you improve inventory management and efficiency without getting buried in overhead.',
      features: [
        'Inventory control and visibility',
        'Climate and temperature-controlled environments',
        'Data and physical security',
        'Reporting, measurement and compliance',
        'Product receiving/put-away',
        'Inbound value-added services',
        'Integrated transportation management'
      ]
    },
    {
      icon: Heart,
      title: 'Medical & Healthcare Logistics',
      description: 'We have established ourselves as the first choice of hospitals, laboratories, pharmacies, public health departments, doctors\' offices, clinics, blood banks, and medical wholesalers.',
      services: [
        {
          name: 'Pharmaceuticals',
          desc: 'We ensure patients and facilities receive their medications in a timely manner.'
        },
        {
          name: 'Medical Supplies',
          desc: 'Supporting various facilities with needed equipment and PPE deliveries.'
        },
        {
          name: 'Biological Specimens',
          desc: 'Labs and other specimens are safely transported in temperature-controlled containers.'
        },
        {
          name: 'Confidential Records',
          desc: 'Confidentiality is of the utmost importance. All drivers are HIPAA trained.'
        },
        {
          name: 'White Glove',
          desc: 'Delivery & installation in hospitals, medical clinics, and home delivery.'
        }
      ]
    },
    {
      icon: Truck,
      title: 'Dedicated Routes',
      description: 'Imagine your own private fleet, efficiently operated by Sabey.',
      details: 'We\'ll handle equipment planning, route optimization, driver hiring, labor management, liability risk management and regulatory compliance for your fleet. You work on winning more business.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 to-slate-700 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
                Your Trusted<br />Logistics Partner
              </h1>
              <p className="text-lg sm:text-xl text-slate-200 mb-8">
                Sabey Logistics delivers excellence in critical logistics with nationwide coverage, 
                advanced technology, and an unwavering commitment to your success.
              </p>
              <div className="flex flex-wrap gap-6">
                <div>
                  <div className="text-4xl font-bold">8M+</div>
                  <div className="text-slate-300">Orders Delivered</div>
                </div>
                <div>
                  <div className="text-4xl font-bold">140+</div>
                  <div className="text-slate-300">Satisfied Customers</div>
                </div>
                <div>
                  <div className="text-4xl font-bold">99.8%</div>
                  <div className="text-slate-300">On-Time Rate</div>
                </div>
              </div>
            </div>
            <div className="hidden lg:block">
              <img
                src="https://customer-assets.emergentagent.com/job_sabey-logistics/artifacts/o3djhb7b_image.png"
                alt="Sabey Logistics Warehouse"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">Our Story</h2>
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-6">
            Sabey Logistics was founded with a simple mission: deliver critical supplies when and where 
            they're needed most. What started as a small courier service has grown into a comprehensive 
            logistics partner serving industries across America.
          </p>
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
            Today, we're proud to be the logistics backbone for healthcare facilities, manufacturers, 
            and enterprises that demand nothing less than excellence. Our network of Field Stocking 
            Locations (FSLs), combined with our dedicated team, ensures your critical deliveries 
            arrive on time, every time.
          </p>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 text-center mb-12">Our Core Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="p-6 bg-white border-0 shadow-lg hover:shadow-xl transition-shadow text-center">
                  <div className="bg-slate-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="h-8 w-8 text-slate-700" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{value.title}</h3>
                  <p className="text-slate-600 text-sm sm:text-base">{value.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* What We Do Best */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 text-center mb-4">What We Do Best</h2>
          <p className="text-lg text-slate-600 text-center mb-12 max-w-3xl mx-auto">
            From service parts to medical supplies, we've built specialized capabilities to meet your most demanding logistics challenges.
          </p>
          
          <div className="space-y-16">
            {capabilities.map((cap, index) => {
              const Icon = cap.icon;
              return (
                <div key={index} className="bg-slate-50 rounded-2xl p-8 sm:p-10">
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="bg-slate-800 p-3 rounded-xl flex-shrink-0">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-slate-900 mb-2">{cap.title}</h3>
                      <p className="text-slate-600 text-lg">{cap.description}</p>
                    </div>
                  </div>
                  
                  {cap.details && (
                    <p className="text-slate-600 mb-6 pl-0 sm:pl-16">{cap.details}</p>
                  )}
                  
                  {cap.features && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pl-0 sm:pl-16">
                      {cap.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                          <span className="text-slate-700 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {cap.services && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pl-0 sm:pl-16">
                      {cap.services.map((svc, idx) => (
                        <div key={idx} className="bg-white p-4 rounded-lg shadow-sm">
                          <div className="font-semibold text-slate-900 mb-1">{svc.name}</div>
                          <div className="text-sm text-slate-600">{svc.desc}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-12">Certifications & Compliance</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {['TSA Certified', 'HAZMAT Trained', 'HIPAA Compliant', 'DOT Certified'].map((cert, index) => (
              <div key={index} className="flex flex-col items-center">
                <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center mb-4">
                  <Shield className="h-10 w-10 text-slate-400" />
                </div>
                <span className="font-semibold">{cert}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
            Ready to Partner with Sabey?
          </h2>
          <p className="text-base sm:text-lg text-slate-600 mb-8">
            Let's discuss how we can support your logistics needs with reliability, speed, and expertise.
          </p>
          <Link to="/contact">
            <Button className="bg-slate-800 hover:bg-slate-700 text-white px-8 py-6 text-lg rounded-lg">
              Contact Us Today
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
